import { gql } from 'graphql-request';
import { useQuery } from '@tanstack/react-query';
import useGraphQLClient from '../../../app/hooks/useGraphQLClient';
import { useAuthContext } from '../../auth/contexts/AuthContext';
import moment from 'moment';
import IDocumentAccess from '../interfaces/IDocumentAccess';
import useAppLog from '../../../app/features/log/hooks/useAppLog';

const useDocumentAccess = (startDate: Date, endDate: Date) => {
	const authContext = useAuthContext();
	const graphQLClient = useGraphQLClient();

	const appLog = useAppLog();
	const startDateChecked = startDate && moment(startDate).format('YYYY-MM-DD');
	const endDateChecked = endDate && moment(endDate).format('YYYY-MM-DD');
	const query = useQuery<IDocumentAccess[] | null, Error>(
		['DocumentAccess', startDateChecked, endDateChecked],
		async () => {
			try {
				const data = (await graphQLClient.request(
					gql`
						query ($startDate: Date, $endDate: Date) {
							documentAccess(startDate: $startDate, endDate: $endDate) {
								document {
									id
									name
									fileName
									documentType
									documentFormat
									releaseDate
									version
								}
								fund {
									id
									name
									typeTag
								}
								company {
									id
									name
									group {
										id
										name
									}
								}
								accessTime
								isImpersonated
							}
						}
					`,
					{
						startDate: startDateChecked,
						endDate: endDateChecked,
					},
				)) as { documentAccess: IDocumentAccess[] };
				return data.documentAccess;
			} catch (e) {
				appLog.exception(e, { component: 'useDocumentAccess' });
				return null;
			}
		},
		{
			// The query will not execute until
			enabled:
				appLog && !!startDate && !!endDate && authContext.isInvestor === false && authContext.isAuthenticated,
		},
	);

	return query;
};

export default useDocumentAccess;
